import { useSignal } from '@preact/signals';
import { Dialog, Notice } from '@amumin/ui';
import { Icon } from '@amumin/ui-core';
import { appText } from '../state/session.js';

interface Space {
  id: string;
  name: string;
  personal?: boolean;
  role: string;
}
export function SpaceSwitcher({
  spaces,
  activeId,
  onSwitch,
}: {
  spaces: Space[];
  activeId: string | null;
  onSwitch: (id: string) => Promise<void>;
}) {
  const open = useSignal(false);
  const busy = useSignal(false);
  const error = useSignal(false);
  const w = appText.value.omgeving.spaces;
  const active = spaces.find((space) => space.id === activeId);
  const options =
    spaces.some((space) => space.personal) ||
    !spaces.some((space) => ['owner', 'parent'].includes(space.role))
      ? spaces
      : [...spaces, { id: 'personal', name: w.mine, personal: true, role: 'owner' }];
  const choose = async (id: string) => {
    if (busy.value) return;
    if (id === activeId) {
      open.value = false;
      return;
    }
    busy.value = true;
    error.value = false;
    try {
      await onSwitch(id);
      open.value = false;
    } catch {
      error.value = true;
    } finally {
      busy.value = false;
    }
  };
  return (
    <>
      <button
        type="button"
        class="space-switcher"
        aria-label={w.switchLabel}
        aria-haspopup="dialog"
        aria-expanded={open.value}
        onClick={() => {
          error.value = false;
          open.value = true;
        }}
      >
        <span class="space-switcher-text">
          <span class="space-switcher-name">
            {active?.personal ? w.mine : (active?.name ?? w.choose)}
          </span>
          <span class="space-switcher-kind">{active?.personal ? w.private : w.shared}</span>
        </span>
        <Icon id="chevron-down" size={16} />
      </button>
      <Dialog
        open={open.value}
        onClose={() => {
          if (!busy.value) open.value = false;
        }}
        title={w.dialogTitle}
        description={w.dialogBody}
        closeLabel={w.close}
        name="space-switcher"
      >
        <div class="space-options" aria-busy={busy.value}>
          {options.map((space) => (
            <button
              key={space.id}
              type="button"
              class="space-option"
              disabled={busy.value}
              aria-current={space.id === activeId ? 'true' : undefined}
              onClick={() => void choose(space.id)}
            >
              <span class="space-option-icon">
                <Icon id={space.personal ? 'lock' : 'users'} size={22} />
              </span>
              <span class="space-option-text">
                <span class="space-option-name">{space.personal ? w.mine : space.name}</span>
                <span class="space-option-description">
                  {space.personal ? w.privateHint : w.sharedHint}
                </span>
              </span>
              {space.id === activeId ? (
                <span class="space-option-check" aria-label={w.current}>
                  <Icon id="check" size={20} />
                </span>
              ) : null}
            </button>
          ))}
        </div>
        {busy.value ? <p role="status">{w.opening}</p> : null}
        {error.value ? <Notice kind="error">{w.failed}</Notice> : null}
      </Dialog>
    </>
  );
}
